<template>
  <b-modal
    id="export-report-modal"
    modal-class="timesheet-modal"
    title="Generate Timesheet Report"
    size="lg"
    ok-title="Export"
    @show="$emit('show')"
    @ok="$emit('ok', $event)"
  >
    <div class="export-modal-body">
      <div class="export-section">
        <label class="export-section-label">Project</label>
        <!-- Custom dropdown instead of a native select so the list always
             opens downwards (browsers flip native selects upwards when
             there is more room above). -->
        <b-dropdown
          block
          no-flip
          variant="light"
          class="export-project-dropdown"
          toggle-class="export-project-toggle"
          menu-class="export-project-menu"
        >
          <template #button-content>{{ projectLabel }}</template>
          <b-dropdown-item-button
            v-for="opt in projectOptions"
            :key="String(opt.value)"
            :active="opt.value === exportOptions.exportProjectId"
            @click="exportOptions.exportProjectId = opt.value"
          >
            {{ opt.text }}
          </b-dropdown-item-button>
        </b-dropdown>
      </div>

      <div class="export-section export-section-row">
        <div class="export-field">
          <label class="export-section-label">From Date</label>
          <b-form-datepicker
            v-model="exportOptions.dateFrom"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
          />
        </div>
        <div class="export-field">
          <label class="export-section-label">To Date</label>
          <b-form-datepicker
            v-model="exportOptions.dateTo"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
          />
        </div>
      </div>

      <div class="export-section">
        <label class="export-section-label">Statuses</label>
        <div class="export-checks-row export-status-list">
          <b-form-checkbox v-model="exportOptions.statuses" value="clocked_in"
            >Clocked In</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.statuses" value="clocked_out"
            >Pending Approval</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.statuses" value="approved"
            >Approved</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.statuses" value="rejected"
            >Rejected</b-form-checkbox
          >
        </div>
      </div>

      <div class="export-section">
        <label class="export-section-label">Leave Types</label>
        <div class="export-checks-row export-status-list">
          <b-form-checkbox
            v-model="exportOptions.leaveTypes"
            value="public_holiday"
            >Public Holiday</b-form-checkbox
          >
          <b-form-checkbox
            v-model="exportOptions.leaveTypes"
            value="personal_leave"
            >Personal Leave</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.leaveTypes" value="sick_leave"
            >Sick Leave</b-form-checkbox
          >
          <b-form-checkbox
            v-model="exportOptions.leaveTypes"
            value="annual_leave"
            >Annual Leave</b-form-checkbox
          >
        </div>
      </div>

      <div class="export-section">
        <label class="export-section-label"
          >Tradies (leave blank for all)</label
        >
        <b-input-group size="sm" class="export-search">
          <b-input-group-prepend>
            <b-input-group-text>
              <b-icon-search />
            </b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            :value="tradieSearch"
            placeholder="Search tradies"
            autocomplete="off"
            @input="$emit('update:tradieSearch', $event)"
          />
        </b-input-group>
        <div class="export-list-card">
          <div class="export-list-meta">
            <span>{{ filteredTradieOptions.length }} shown</span>
            <span v-if="exportOptions.tradieIds.length"
              >{{ exportOptions.tradieIds.length }} selected</span
            >
          </div>
          <div v-if="filteredTradieOptions.length" class="export-tradie-list">
            <div
              v-for="(column, colIndex) in tradieColumns"
              :key="colIndex"
              class="export-tradie-column"
            >
              <b-form-checkbox
                v-for="opt in column"
                :key="opt.value"
                v-model="exportOptions.tradieIds"
                :value="opt.value"
              >
                {{ opt.text }}
              </b-form-checkbox>
            </div>
          </div>
          <div v-else class="export-empty-state">
            No tradies match your search.
          </div>
        </div>
      </div>

      <div class="export-section">
        <label class="export-section-label">Columns to Include</label>
        <div class="export-checks-grid">
          <b-form-checkbox v-model="exportOptions.columns.date"
            >Date</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.tradieName"
            >Tradie Name</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.project"
            >Project</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.zone"
            >Zone</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.startTime"
            >Start Time</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.endTime"
            >End Time</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.hours"
            >Hours</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.breakMinutes"
            >Break (mins)</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.status"
            >Status</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.comment"
            >Work Comments</b-form-checkbox
          >
          <b-form-checkbox v-model="exportOptions.columns.shift"
            >Day / Night</b-form-checkbox
          >
        </div>
      </div>

      <div class="export-section export-section-row">
        <div class="export-field">
          <label class="export-section-label">Sort By</label>
          <b-form-select
            v-model="exportOptions.sortBy"
            :options="[
              { value: 'date', text: 'Date - Newest First' },
              { value: 'date_asc', text: 'Date - Oldest First' },
              { value: 'tradie', text: 'Tradie Name' },
              { value: 'project', text: 'Project' },
              { value: 'status', text: 'Status' },
            ]"
            size="sm"
          />
        </div>
        <div class="export-field">
          <label class="export-section-label">Export As</label>
          <div class="export-checks-row">
            <b-form-radio v-model="exportOptions.format" value="csv"
              >CSV</b-form-radio
            >
            <b-form-radio v-model="exportOptions.format" value="pdf"
              >PDF</b-form-radio
            >
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'TimesheetExportModal',
  props: {
    exportOptions: { type: Object, required: true },
    tradieSearch: { type: String, default: '' },
    projectOptions: { type: Array, default: () => [] },
    projectLabel: { type: String, default: 'All Projects' },
    filteredTradieOptions: { type: Array, default: () => [] },
    tradieColumns: { type: Array, default: () => [] },
  },
  data() {
    return {
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
    }
  },
}
</script>

<style lang="scss" scoped>
.export-modal-body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 20px;
  overflow-x: hidden;
}

.export-section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.export-section-row {
  flex-direction: row;
  /* Wrap on narrow phones: fields drop to their own full-width line instead
     of pushing past the modal edge. */
  flex-wrap: wrap;
  gap: 16px 32px;
  align-items: flex-start;
}

.export-field {
  display: flex;
  flex: 1 1 160px;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.export-section-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0;
}

.export-checks-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.export-checks-grid {
  display: grid;
  /* As many columns as genuinely fit (up to 3) — collapses to 2 then 1 on
     narrow screens instead of squashing the labels. */
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px 16px;
}

.export-search ::v-deep .input-group-text {
  border-color: #dbe3ef;
  background: #f8fafc;
  color: #64748b;
}

.export-search ::v-deep .form-control {
  border-color: #dbe3ef;
  box-shadow: none;
}

.export-list-card {
  min-width: 0;
  padding: 10px;
  border: 1px solid #e5eaf2;
  border-radius: 12px;
  background: #f8fafc;
}

.export-list-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.export-tradie-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px 16px;
  max-height: 160px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 2px;
}

.export-tradie-column {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.export-tradie-column ::v-deep .custom-control {
  min-width: 0;
  max-width: 100%;
}

.export-tradie-column ::v-deep .custom-control-label {
  max-width: 100%;
  color: #1f2937;
  line-height: 1.3;
  overflow-wrap: anywhere;
  white-space: normal;
}

.export-empty-state {
  padding: 18px 10px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}

/* Phones: one status per line — mixed wrapping (two on a line, then one)
   reads messily in the narrow modal. */
@media (max-width: 575.98px) {
  .export-tradie-list {
    grid-template-columns: 1fr;
  }

  .export-status-list {
    flex-direction: column;
    gap: 8px;
  }
}

/* Project dropdown: styled to match the small form selects around it */
.export-project-dropdown ::v-deep .export-project-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 0.2rem;
  background: #fff;
  color: #495057;
  font-size: 0.875rem;
  font-weight: 400;
}

.export-project-dropdown ::v-deep .export-project-menu {
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  font-size: 0.875rem;
}
</style>
